import { ContextKindKey } from '@gonfalon/contexts/types';
import {
  enforceResourceNameLength,
  userTargetAlertThreshold,
  userTargetWarningThreshold,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { MAX_FLAG_NAME_LENGTH } from '@gonfalon/flag-creation/validation';
import { getFlagVariationIndexFromId } from '@gonfalon/flag-variations/getFlagVariationIndexFromId';
import { canDeprecateFlag } from '@gonfalon/flags/canDeprecateFlag';
import { createMissingEnvironmentError } from '@gonfalon/flags/errors';
import { getFlagActiveVariationsFromConfig } from '@gonfalon/flags/getFlagActiveVariationsFromConfig';
import { getFlagActiveVariationsFromSummary } from '@gonfalon/flags/getFlagActiveVariationsFromSummary';
import { isFlagDeprecated } from '@gonfalon/flags/isFlagDeprecated';
import { isFlagOnForEnvironment } from '@gonfalon/flags/isFlagOnForEnvironment';
import type { FeatureFlagConfig } from '@gonfalon/flags/types';
// eslint-disable-next-line no-restricted-imports
import { fromJS, is, List, Map, Record, Set } from '@gonfalon/immutable-shim/immutableShim';
import {
  areAllUnique,
  isLength,
  isNotEmpty,
  isReservedKey,
  isValidEach,
  isValidEachKeyed,
  isValidTagList,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { toJS } from '@gonfalon/legacy-immutable/immutableUtils';
import { isValidKey } from '@gonfalon/strings/isValidKey';
import { isJSONValue } from '@gonfalon/types/isJSONValue';
import { stringifyValue } from '@gonfalon/types/stringifyValue';
import { isEqual, noop } from 'lodash';
import invariant from 'tiny-invariant';

import { allowDecision } from '#utils/access/allowDecision';
import { combineAccessDecisions } from '#utils/access/combineAccessDecisions';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { CheckAccessFunction, DeniedAccess } from '#utils/access/types';
import type { Clause } from '#utils/clause/records/Clause';
import { createClause } from '#utils/clause/records/Clause';
import { createAllCodeRefStatsRecord } from '#utils/codeRefs/createAllCodeRefStatsRecord';
import type { AllCodeRefStats } from '#utils/codeRefs/types';
import type { ApprovalSettings } from '#utils/environments/records/ApprovalSettings';
import { customPropertiesFormToRep, customPropertiesToForm } from '#utils/flags/customProperties';
import type { FlagKind } from '#utils/flags/flagConstants';
import { flagKinds, VariationType, variationTypes } from '#utils/flags/flagConstants';
import { isAiConfigFlag, isMigrationFlag } from '#utils/flags/flagPurpose';
import type {
  ClientSideAvailability,
  CustomProperty,
  FlagMigrationSettings,
  FlagPurpose,
  VariationValue,
} from '#utils/flags/flagTypes';
import { RuleLabel } from '#utils/flags/flagTypes';
import { handleRolloutLogic } from '#utils/flags/handleRolloutLogic';
import { idOrKey } from '#utils/flags/idOrKey';
import type { CustomPropertyForm } from '#utils/flags/records/CustomPropertyForm';
import { createCustomPropertyForm } from '#utils/flags/records/CustomPropertyForm';
import { Defaults } from '#utils/flags/records/Defaults';
import type { EnvironmentFlagSettings } from '#utils/flags/records/EnvironmentFlagSettings';
import type { Experiment } from '#utils/flags/records/Experiment';
import { createExperimentInfo, ExperimentInfo } from '#utils/flags/records/ExperimentInfo';
import { Fallthrough } from '#utils/flags/records/Fallthrough';
import type { FlagConfigSetting, FlagConfiguration } from '#utils/flags/records/FlagConfiguration';
import { createFlagConfiguration } from '#utils/flags/records/FlagConfiguration';
import { FlagMaintainer } from '#utils/flags/records/FlagMaintainer';
import type { MeasuredRolloutConfig } from '#utils/flags/records/MeasuredRolloutConfig';
import type { Prerequisite } from '#utils/flags/records/Prerequisite';
import { createPrerequisite } from '#utils/flags/records/Prerequisite';
import type { ExperimentRollout } from '#utils/flags/records/Rollout';
import { createRollout } from '#utils/flags/records/Rollout';
import type { Rule } from '#utils/flags/records/Rule';
import { createRule } from '#utils/flags/records/Rule';
import { createRuleExclusionRecord } from '#utils/flags/records/RuleExclusionSettings';
import type { Target } from '#utils/flags/records/Target';
import { createTarget } from '#utils/flags/records/Target';
import type { Variation } from '#utils/flags/records/Variation';
import { createVariation } from '#utils/flags/records/Variation';
import { detectVariationType, forceVariationType, inferVariationType } from '#utils/flags/variationTypes';
import type { WeightedVariation } from '#utils/flags/weightedVariationUtils';
import { createWeightedVariation } from '#utils/flags/weightedVariationUtils';
import type { FlagStatus } from '#utils/flagStatusUtils';
import type { Link } from '#utils/linkUtils';
import type { Member } from '#utils/members/records/Member';
import type { MemberSummary } from '#utils/members/records/MemberSummary';
import { createMemberSummary } from '#utils/members/records/MemberSummary';
import type { Team } from '#utils/teams/records/Team';
import { createTeam } from '#utils/teams/records/Team';
import { validateFlagConfiguration } from '#utils/validation/flags';

import { setRolloutForRule } from '../setRolloutForRule';

export type FlagType = {
  _links: ImmutableMap<{
    self: Link;
    parent: Link;
  }>;
  _maintainer?: Member | MemberSummary;
  _maintainerTeam?: Team;
  _site: Link;
  _version: number;
  archived: boolean;
  archivedDate?: number;
  clientSideAvailability?: ClientSideAvailability;
  codeReferences?: AllCodeRefStats;
  creationDate: number;
  customProperties: List<CustomPropertyForm>;
  // customProperties: OrderedMap<string, CustomPropertyForm>; // The API returns CustomProperty, but we use CustomPropertyForm in the UI (for now…)
  defaults?: Defaults;
  description?: string;
  environments: Map<string, FlagConfiguration>;
  experiments: ExperimentInfo;
  goalIds: Set<string>;
  key: string;
  kind: FlagKind;
  maintainerId?: string;
  maintainerTeamKey?: string;
  name: string;
  tags: Set<string>;
  temporary: boolean;
  variations: List<Variation>;
  _purpose?: FlagPurpose;
  migrationSettings?: Map<string, FlagMigrationSettings>;
  releasePipelineKey?: string;
  deprecated: boolean;
  deprecatedDate?: number;
};

const flagConfigContainsTargetsOrRules = (flagConfig: FeatureFlagConfig) => {
  const { targets, rules, contextTargets } = flagConfig;
  return (
    (targets && targets.length > 0) || (rules && rules.length > 0) || (contextTargets && contextTargets.length > 0)
  );
};

export class Flag extends Record<FlagType>({
  _links: Map(),
  _maintainer: undefined,
  _maintainerTeam: undefined,
  _site: Map(),
  _version: 0,
  archived: false,
  archivedDate: undefined,
  clientSideAvailability: undefined,
  codeReferences: undefined,
  creationDate: 0,
  customProperties: customPropertiesToForm(Map()),
  // customProperties: OrderedMap(),
  defaults: undefined,
  description: undefined,
  deprecated: false,
  deprecatedDate: undefined,
  environments: Map(),
  experiments: new ExperimentInfo(),
  goalIds: Set(),
  key: '',
  kind: flagKinds.BOOLEAN,
  maintainerId: undefined,
  maintainerTeamKey: undefined,
  name: '',
  tags: Set(),
  temporary: true,
  variations: List(),
  _purpose: undefined,
  migrationSettings: undefined,
  releasePipelineKey: undefined,
}) {
  static ALLOWED_ARCHIVABLE_AGE_IN_MONTHS = 3;

  clearGuardedRolloutFromRule(environmentKey: string, rule: Rule | Fallthrough): Flag {
    if (rule instanceof Fallthrough) {
      return this.setFallthroughMeasuredRolloutConfig(environmentKey, undefined);
    }

    return this.setRuleMeasuredRolloutConfig(environmentKey, rule, undefined);
  }

  getDefaultRolloutVariation(controlVariationId?: string, testVariationId?: string): Variation | undefined {
    if (testVariationId && testVariationId !== controlVariationId) {
      // If there is a test variation, and it is valid, return it
      return this.variations.find((variation: Variation) => variation._id === testVariationId);
    }
    // Find first variation that isn't the control variation
    return this.variations.find((variation: Variation) => variation._id !== controlVariationId);
  }

  getStageCount() {
    return this.getIn(['migrationSettings', 'stageCount']);
  }

  getMigrationContextKind() {
    return this.getIn(['migrationSettings', 'contextKind']);
  }

  isMigrationFlag() {
    return isMigrationFlag(this.get('_purpose'));
  }

  isAIFlag() {
    return this._purpose === 'ai';
  }

  supportsPrerequisites() {
    return !this.isAIFlag() && !(this.isMigrationFlag() && this.getStageCount() === 6);
  }

  getRuleLabel() {
    return this.isMigrationFlag() ? RuleLabel.COHORT : RuleLabel.RULE;
  }

  // Returns the maintainer, regardless of whether they are a member or team.
  getMaintainer(): FlagMaintainer | undefined {
    if (this.maintainerTeamKey) {
      // Team maintainer
      return new FlagMaintainer({
        idOrKey: this._maintainerTeam?.key,
        kind: 'team',
        member: undefined,
        name: this._maintainerTeam?.name,
        team: this._maintainerTeam,
      });
    }
    if (this.maintainerId) {
      // Member maintainer
      return new FlagMaintainer({
        idOrKey: this._maintainer?._id,
        kind: 'member',
        member: this._maintainer,
        name: this._maintainer?.getDisplayName(),
        team: undefined,
      });
    }
  }

  checkAccess({ envKey, profile }: { envKey: string; profile: Member }): CheckAccessFunction {
    const access = this.getIn(['environments', envKey, '_access']);
    if (profile?.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }
    if (profile?.hasStrictWriterRights() || !(access && access.get('denied'))) {
      return allowDecision;
    }
    return (action) => {
      const deniedAction = access.get('denied').find((v: DeniedAccess) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  checkAccessForAllEnvironments({ profile }: { profile: Member }): CheckAccessFunction {
    return (action) => {
      const accessDecisions = this.environments
        .keySeq()
        .toArray()
        .map((envKey) => this.checkAccess({ envKey, profile })(action));

      return accessDecisions.length > 0
        ? combineAccessDecisions(...accessDecisions)
        : createAccessDecision({ isAllowed: false });
    };
  }

  addCustomPropertyValue(customPropertyKey: string, customPropertyValue: string) {
    return this.set(
      'customProperties',
      customPropertiesToForm(
        customPropertiesFormToRep(this.customProperties).update(
          customPropertyKey,
          fromJS({
            key: customPropertyKey,
            name: customPropertyKey.replace('.', ' ').replace(/^\w/, (c) => c.toUpperCase()),
            value: [customPropertyValue],
          }),
          (cmp) => cmp.update('value', (v) => (v as Set<string>).toSet().add(customPropertyValue)),
        ),
      ),
    );
  }

  hasExperiments() {
    const hasNewExperiments = !!(this.experiments && this.experiments.items.size);
    return !this.goalIds.isEmpty() || hasNewExperiments;
  }

  hasActiveExperiments(environmentKey: string) {
    return this.experiments?.items.some((metric) => {
      const envSetting = metric._environmentSettings.get(environmentKey);
      return !!(envSetting && envSetting.startDate && !envSetting.stopDate);
    });
  }

  hasNonPristineExperiments(environmentKey: string) {
    return (
      !!this.experiments &&
      this.experiments.items.reduce((hasActives, item) => {
        if (hasActives) {
          return hasActives;
        }
        return !!item._environmentSettings.get(environmentKey)?.startDate;
      }, false)
    );
  }

  getNonPristineExperimentsCount(environmentKey: string) {
    if (!this.experiments) {
      return 0;
    }
    return this.experiments.items.reduce(
      (accum, item) => (item._environmentSettings.get(environmentKey)?.startDate ? accum + 1 : accum),
      0,
    );
  }

  hasNonPristineExperimentsInAllEnvs() {
    if (!this.experiments) {
      return false;
    }
    return this.experiments.items.some((item) => {
      const startedEnvKeys = item._environmentSettings.filter(
        (settings, environmentKey) => item._environmentSettings.get(environmentKey)?.startDate,
      );
      return startedEnvKeys.size > 0;
    });
  }

  getExperimentStartDate(metricKey: string, environmentKey: string) {
    const experiment = this.experiments.items.find((item) => item.metricKey === metricKey);
    return experiment?.get('_environmentSettings').get(environmentKey)?.get('startDate');
  }

  isExperimentActive(metricKey: string, environmentKey: string) {
    const experiment = this.experiments.items.find((item) => item.metricKey === metricKey);
    return experiment ? !!experiment.get('environments').find((env) => env === environmentKey) : false;
  }

  updateExperimentEnvironments(metricKey: string, updateFunction: (environments: List<string>) => List<string>) {
    return this.updateIn(['experiments', 'items'], (associatedMetrics: List<Experiment>) =>
      associatedMetrics.map((associated) => {
        if (associated.metricKey !== metricKey) {
          return associated;
        }

        return associated.update('environments', (envs) => updateFunction(envs));
        // };
      }),
    );
  }

  selfLink() {
    return this._links?.get('self')?.get('href');
  }

  siteLink(environmentKey: string) {
    // Incident: https://launchdarkly.slack.com/archives/C03L9437YGG
    // At least one customer had a flag with missing environment configurations.
    // This ensures we don't give React Router (v6) an undefined link, which
    // results in a runtime exception.
    return this.readConfiguration(environmentKey, (cfg) => cfg._site.get('href')) || '';
  }

  getKind() {
    if (this.has('kind')) {
      return this.kind;
    }

    const variationType = inferVariationType(this);

    if (this.variations.size === 2 && variationType === variationTypes.BOOLEAN) {
      return flagKinds.BOOLEAN;
    } else {
      return flagKinds.MULTIVARIATE;
    }
  }

  getVariations() {
    return this.variations;
  }

  getBaselineIndex() {
    return this.getIn(['experiments', 'baselineIdx']);
  }

  isBoolean() {
    return this.getKind() === flagKinds.BOOLEAN;
  }

  validate({ variationType }: { variationType?: VariationType } = {}) {
    let predicates = [
      isNotEmpty('name'),
      enforceResourceNameLength() ? isLength(1, MAX_FLAG_NAME_LENGTH)('name') : noop,
      isNotEmpty('key'),
      isValidKey('key'),
      isReservedKey('key'),
      isValidEachKeyed(
        (v: Variation) => v._key,
        (v: Variation) => createVariation(v).validate({ requiredType: variationType }).toJS(),
        'variations',
      ),
    ];

    predicates = [
      ...predicates,
      isValidTagList('tags'),
      areAllUnique(
        (v: Variation) => v.value,
        (v: Variation) => !createVariation(v).isEmpty() || v.value === '',
      )(() => "You can't create variations with the same value", 'variations'),

      // Empty strings for names are fine, but any non-empty string must be unique
      areAllUnique(
        (v) => v.name,
        (v) => !createVariation(v).isEmpty() && v.name !== '',
      )(() => "You can't create variations with the same name.", 'variations'),

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      isValidEach((m: any) => createCustomPropertyForm(m).validate().toJS(), 'customProperties'),
      areAllUnique(
        (p) => p.key,
        (p) => !Map(p).isEmpty(),
      )(() => "You can't create custom properties with the same key", 'customProperties'),
    ];
    return validateRecord(this, ...predicates);
  }

  addVariation(variation: Variation) {
    return this.update('variations', (variations) => variations.push(variation));
  }

  addNewVariation(variationType: VariationType) {
    if (variationType === VariationType.NUMBER) {
      return this.update('variations', (variations) =>
        // NumberField from react-aria-components, which we use to render number variation values,
        // only considers NaN to be a truely empty value, not the empty string
        variations.push(createVariation({ _isDraft: true, value: NaN })),
      );
    }
    return this.update('variations', (variations) => variations.push(createVariation({ _isDraft: true })));
  }

  editVariation(variation: Variation) {
    const index = this.variations.findIndex((it) => it._key === variation._key);
    return this.updateIn(['variations', index], (it) => it.merge(variation));
  }

  removeVariation(variation: Variation) {
    const index = this.variations.findIndex((it) => it._key === variation._key);
    return this.deleteIn(['variations', index]);
  }

  getVariationType() {
    return detectVariationType(this);
  }

  getPotentialVariationType() {
    return inferVariationType(this);
  }

  tryForcingVariationType(t: VariationType) {
    if (this.getVariationType() !== t && this.getPotentialVariationType() !== t) {
      return this;
    }

    return forceVariationType(this, t);
  }

  getConfiguration(environmentKey: string) {
    const config = this.getIn(['environments', environmentKey]);

    if (!config) {
      throw createMissingEnvironmentError(environmentKey);
    }

    return createFlagConfiguration(config);
  }

  hasConfigurationForEnvironment(environmentKey: string) {
    return !!this.getIn(['environments', environmentKey]);
  }

  getFallthrough(environmentKey: string) {
    const config = this.getConfiguration(environmentKey);
    return config?.get('fallthrough');
  }

  setFallthrough(environmentKey: string, fallthrough: Fallthrough) {
    return this.updateConfiguration(environmentKey, (config) => config.set('fallthrough', fallthrough));
  }

  readConfiguration<T>(environmentKey: string, readerFn: (config: FlagConfiguration) => T) {
    const config = this.getIn(['environments', environmentKey]);
    return config ? readerFn(config) : undefined;
  }

  updateConfiguration(environmentKey: string, updaterFn: (config: FlagConfiguration) => FlagConfiguration) {
    return this.updateIn(['environments', environmentKey], (cfg) => updaterFn(cfg));
  }

  getRuleExclusionForm(environmentKey: string) {
    return createRuleExclusionRecord(this.getConfiguration(environmentKey));
  }

  updateFromRuleExclusionForm(environmentKey: string, nextSettings: EnvironmentFlagSettings) {
    return this.updateConfiguration(environmentKey, (cfg) => cfg.merge(nextSettings));
  }

  toggle(environmentKey: string) {
    return this.updateConfiguration(environmentKey, (c) => c.update('on', (on) => !on));
  }

  isOn(environmentKey: string) {
    const config = this.getIn(['environments', environmentKey]);
    invariant(config, 'Missing flag configuration for environment');
    return isFlagOnForEnvironment(config);
  }

  getActiveVariationsFromSummary(environmentKey: string): Map<number, Variation> | undefined {
    const config = this.getIn(['environments', environmentKey]);
    return config
      ? Map(getFlagActiveVariationsFromSummary(config.toJS(), this.variations.toJS())).map((v) => createVariation(v))
      : undefined;
  }

  getActiveVariationsFromConfig(environmentKey: string): Map<number, Variation> | undefined {
    const config = this.getIn(['environments', environmentKey]);
    return config
      ? Map(
          getFlagActiveVariationsFromConfig(
            this.getIn(['environments', environmentKey]).toJS(),
            this.variations.toJS(),
          ),
        ).map((v) => createVariation(v))
      : undefined;
  }

  getActiveVariationsContainingSegment(environmentKey: string, segmentKey: string): Map<number, Variation> | undefined {
    return this.readConfiguration(environmentKey, (config) => {
      if (!config) {
        return Map();
      }

      if (!this.isOn(environmentKey)) {
        if (config.offVariation === undefined) {
          return Map();
        }
        const offVariation = this.variations.get(config.offVariation);
        if (offVariation === undefined) {
          return Map();
        }

        const variationIndex = this.getVariationIndexFromId(offVariation._id);
        return Map([[variationIndex, offVariation]]);
      }
      // This logic also lives on the backend in internal/reps2/flag_rep2.go#MakeConfigurationRep.
      // If you add or remove variation types here, you may also need to update on the backend.
      const activeVariationIndexes = this.getVariationsContainingSegment(config, segmentKey);

      // then turn it into a map with their full-fledged variation objects
      let variations = Map<number, Variation>();
      activeVariationIndexes.forEach((i) => {
        if (i !== undefined) {
          const variation = this.variations.get(i);
          if (variation) {
            variations = variations.set(i, variation);
          }
        }
      });

      return variations;
    });
  }

  getVariationsContainingSegment(config: FlagConfiguration, segmentKey: string): List<number> {
    let ruleVariationIndexes = List<number>();
    config.rules?.forEach((r) => {
      if (r.hasSegment(segmentKey)) {
        if (r.rollout?.variations?.size) {
          // filter out rules where weight is 0 to match backend logic
          const weightedVariations = r.rollout.variations.filter((wv) => wv.weight && wv.weight !== 0);
          ruleVariationIndexes = ruleVariationIndexes.concat(weightedVariations.map((wv) => wv.variation));
        }
        if (r.variation !== undefined) {
          ruleVariationIndexes = ruleVariationIndexes.push(r.variation);
        }
      }
    });
    return ruleVariationIndexes;
  }

  getFallthroughRolloutVariations(config: FlagConfiguration): List<number> {
    let fallthroughRolloutIndexes = List<number>();
    config.fallthrough?.rollout?.variations.forEach((wv) => {
      if (wv.variation !== undefined && wv.weight > 0) {
        fallthroughRolloutIndexes = fallthroughRolloutIndexes.push(wv.variation);
      }
    });
    return fallthroughRolloutIndexes;
  }

  clearOffVariation(environmentKey: string) {
    return this.updateConfiguration(environmentKey, (cfg) => cfg.set('offVariation', undefined));
  }

  setOffVariation(environmentKey: string, variation: Variation) {
    const index = this.variations.findIndex((v) => v._key === variation._key);
    return this.updateConfiguration(environmentKey, (cfg) => cfg.set('offVariation', index));
  }

  getOffVariation(environmentKey: string) {
    return this.readConfiguration(environmentKey, (c) =>
      c.offVariation !== undefined ? this.variations.get(c.offVariation) : undefined,
    );
  }

  getDefaultVariation(status?: FlagStatus) {
    if (!status || status.default === null) {
      return;
    }

    if (this.getVariationType() === variationTypes.JSON && typeof status.default === 'object') {
      // if it's a json flag the immutable maps will return false with ===
      return this.variations.find((v) => is(v.value, status.default));
    }
    return this.variations.find((v) => v.value === status.default);
  }

  addPrerequisite(environmentKey: string) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('prerequisites', (ps) => ps.push(createPrerequisite())),
    );
  }

  setPrerequisites(environmentKey: string, prerequisites: List<Prerequisite>) {
    return this.updateConfiguration(environmentKey, (cfg) => cfg.set('prerequisites', prerequisites));
  }

  deletePrerequisite(environmentKey: string, prerequisite: Prerequisite) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('prerequisites', (ps) => {
        const index = ps.findIndex((p) => p._key === prerequisite._key);
        if (index === -1) {
          return ps;
        }
        return ps.delete(index);
      }),
    );
  }

  editPrerequisite(environmentKey: string, prerequisite: Prerequisite) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('prerequisites', (ps) => {
        const index = ps.findIndex((p) => p._key === prerequisite._key);
        if (index === -1) {
          return ps;
        }
        return ps.set(index, prerequisite);
      }),
    );
  }

  getTargets(environmentKey: string) {
    return this.readConfiguration(environmentKey, (cfg) => cfg.targets);
  }

  getContextTargets(environmentKey: string) {
    return this.readConfiguration(environmentKey, (cfg) => cfg.contextTargets);
  }

  hasTargets(environmentKey: string) {
    const userTargets = this.getTargets(environmentKey);
    const contextTargets = this.getContextTargets(environmentKey);
    return (userTargets && !userTargets.isEmpty()) || (contextTargets && !contextTargets.isEmpty());
  }

  getTarget(environmentKey: string, variationIndex: number) {
    return this.readConfiguration(environmentKey, (cfg) => cfg.getTarget(variationIndex));
  }

  getContextTarget(environmentKey: string, variationIndex: number, contextKind: string) {
    return this.readConfiguration(environmentKey, (cfg) => cfg.getContextTarget(variationIndex, contextKind));
  }

  setTargets(environmentKey: string, targets: List<Target>) {
    return this.updateConfiguration(environmentKey, (cfg) => cfg.set('targets', targets));
  }

  setTarget(environmentKey: string, variation: Variation | undefined, keys: List<string>) {
    invariant(variation, 'Missing variation when attempting to set target');
    return this.updateTargeting(environmentKey, variation, keys, 'user');
  }

  setContextTarget(environmentKey: string, variation: Variation, keys: List<string>, contextKind: string) {
    return this.updateTargeting(environmentKey, variation, keys, contextKind);
  }

  setContextTargets(environmentKey: string, targets: List<Target>) {
    return this.updateConfiguration(environmentKey, (cfg) => cfg.set('contextTargets', targets));
  }

  setFallthroughVariation(environmentKey: string, variation: Variation) {
    const index = this.variations.findIndex((v) => v._key === variation._key);

    if (index === -1) {
      return this;
    }

    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('fallthrough', (ft) => ft.delete('rollout').set('variation', index)),
    );
  }

  //allows multiple rollouts to be set at once
  setFallthroughExperimentRollout(
    environmentKey: string,
    rolloutData: List<WeightedVariation>,
    experimentData: ExperimentRollout,
  ) {
    const variations = this.variations;
    //check that all variations exist
    let variationExists = true;
    rolloutData.forEach((data) => {
      const variation = variations.get(data.variation);
      if (!variation && variationExists) {
        variationExists = false;
      }
    });

    if (!variationExists) {
      return this;
    }

    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('fallthrough', (ft) =>
        ft
          .delete('variation')
          .update('rollout', (r) => {
            const experimentRolloutInfo = { experimentAllocation: experimentData };
            return r && r.experimentAllocation
              ? r.set('experimentAllocation', experimentData)
              : createRollout(experimentRolloutInfo);
          })
          .update('rollout', (r) => {
            /* eslint-disable @typescript-eslint/no-non-null-assertion */
            // We created a fresh rollout above so we can safely assert that it exists here.
            let newR = r!; /* eslint-enable @typescript-eslint/no-non-null-assertion */
            rolloutData.forEach((data) => {
              newR = handleRolloutLogic(newR, data.variation, data.weight, variations);
            });
            return newR;
          }),
      ),
    );
  }

  setFallthroughRollout(environmentKey: string, variation: Variation, weight: number, contextKind?: string) {
    const variationIndex = this.variations.findIndex((v) => v._key === variation._key);
    if (variationIndex === -1) {
      return this;
    }

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('fallthrough', (ft) =>
        ft
          .delete('variation')
          .update('rollout', (r) => (r && !r.experimentAllocation ? r : createRollout({ contextKind })))
          // We initialized the rollout above so we can safely assert it exists here
          .update('rollout', (r) => r!.setWeightForVariation(this, variation, weight)),
      ),
    ); /* eslint-enable @typescript-eslint/no-non-null-assertion */
  }

  getReferenceWeightForFallthroughRuleByIndex(environmentKey: string, index: number) {
    return this.readConfiguration(environmentKey, (cfg) => {
      if (!cfg.fallthrough.rollout) {
        return 0;
      }

      const variation = cfg.fallthrough.rollout.variations.get(index);
      return variation ? variation.weight : 0;
    });
  }

  createWeightedRollout(refVariation: Variation, weight: number, bucketBy?: string, contextKind?: string) {
    const refIndex = this.variations.indexOf(refVariation);
    const otherIndex = this.variations.findIndex((v) => v !== refVariation);
    const refWeightedVariation = createWeightedVariation({
      variation: refIndex,
      weight,
    });
    const otherWeightedVariation = createWeightedVariation({
      variation: otherIndex,
      weight: 100000 - weight,
    });
    return createRollout({
      // The audit log backend expects the true variation to be first, so make sure we're not reordering the variations
      variations:
        refIndex < otherIndex
          ? List.of(refWeightedVariation, otherWeightedVariation)
          : List.of(otherWeightedVariation, refWeightedVariation),
      bucketBy,
      contextKind,
    });
  }

  setFallthroughBucket(environmentKey: string, bucketBy?: string, contextKind?: string) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('fallthrough', (ft) =>
        ft
          .delete('variation')
          .update('rollout', (r) =>
            r
              ? r.set('bucketBy', bucketBy).set('contextKind', contextKind || '')
              : createRollout({ bucketBy, contextKind }),
          ),
      ),
    );
  }

  hasTargetsOrRules(environmentKey: string) {
    if (!this.getConfiguration(environmentKey)) {
      return false;
    }
    return flagConfigContainsTargetsOrRules(this.getConfiguration(environmentKey)?.toJS() as FeatureFlagConfig);
  }

  getRules(environmentKey: string) {
    return this.readConfiguration(environmentKey, (cfg) => cfg.rules);
  }

  setRules(environmentKey: string, rules: List<Rule>) {
    return this.updateConfiguration(environmentKey, (cfg) => cfg.set('rules', rules));
  }

  // get the prerequisites from the flag's config
  // the config updates as the flag updates, so this is the most up-to-date list of prerequisites
  getPrerequisites(environmentKey: string) {
    return this.readConfiguration(environmentKey, (cfg) => cfg.prerequisites);
  }

  // get count of prerequisites from the flag's summary
  // _summary does NOT update as the flag updates, so consider this a snapshot of the flag's current prerequisites
  getPrerequisiteCount(environmentKey: string) {
    const summary = this.readConfiguration(environmentKey, (cfg) => cfg._summary);
    return summary?.prerequisites ?? 0;
  }

  hasPrerequisites(environmentKey: string, useFlagConfiguration?: boolean) {
    if (useFlagConfiguration) {
      const prerequisites = this.getPrerequisites(environmentKey);
      return prerequisites && prerequisites.size > 0;
    }
    return this.getPrerequisiteCount(environmentKey) > 0;
  }

  getRuleById(environmentKey: string, ruleId: string) {
    const rules = this.getRules(environmentKey);
    if (!rules) {
      return undefined;
    }
    return rules.filter((r) => idOrKey(r) === ruleId).first<undefined>();
  }

  getRuleIndex(environmentKey: string, ruleId: string) {
    const rules = this.getRules(environmentKey);
    if (!rules) {
      return -1;
    }
    return rules.findIndex((r) => idOrKey(r) === ruleId);
  }

  addRule(environmentKey: string, defaultClause?: Clause | Clause[], rule?: Rule) {
    if (rule !== undefined) {
      return this.updateConfiguration(environmentKey, (cfg) => cfg.update('rules', (rs) => rs.push(rule)));
    }

    let defaultClauses: List<Clause> | undefined;
    if (defaultClause) {
      defaultClauses = Array.isArray(defaultClause) ? List(defaultClause) : List.of(defaultClause);
    }

    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('rules', (rs) => rs.push(createRule({ clauses: defaultClauses }))),
    );
  }

  deleteRule(environmentKey: string, rule: Rule) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('rules', (rs) => {
        const index = rs.findIndex((r) => r._key === rule._key);
        if (index === -1) {
          return rs;
        }

        return rs.delete(index);
      }),
    );
  }

  duplicateRule(environmentKey: string, rule: Rule) {
    const duplicatedRule = rule
      .set('_key', undefined)
      .set('_id', undefined)
      .set('ref', undefined)
      // Ensures we don't have different rules with the same clause._id or ._key
      .update('clauses', (clauses) => clauses.map((c) => c.set('_key', '').set('_id', '')))
      .update('description', (desc?: string) => {
        const trimmed = (desc ?? '').trim();
        return trimmed ? `${trimmed} (copy)` : '';
      });

    const newRuleIndex = this.getRuleIndex(environmentKey, idOrKey(rule)) + 1;
    const newRule = createRule(duplicatedRule);
    return {
      updatedFlag: this.updateConfiguration(environmentKey, (cfg) =>
        cfg.update('rules', (rs) => rs.insert(newRuleIndex, newRule)),
      ) as Flag,
      newRule,
      newRuleIndex,
    };
  }

  setRuleIndex(environmentKey: string, ruleKey: string, index: number) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('rules', (rules) => {
        const previousIndex = rules.findIndex((r) => r._key === ruleKey);
        if (previousIndex === -1 || index === -1) {
          return rules;
        }

        const rule = rules.get(previousIndex);

        if (!rule) {
          return rules;
        }

        return rules.delete(previousIndex).insert(index, rule);
      }),
    );
  }

  setRuleVariation(environmentKey: string, rule: Rule, variation: Variation) {
    const variationIndex = this.variations.findIndex((v) => v._key === variation._key);
    if (variationIndex === -1) {
      return this;
    }

    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('rules', (rules) => {
        const index = rules.findIndex((r) => r._key === rule._key);
        if (index === -1) {
          return rules;
        }

        return rules.update(index, (r) => r.delete('rollout').set('variation', variationIndex));
      }),
    );
  }

  setRuleRollout(environmentKey: string, rule: Rule, variation: Variation, weight: number, contextKind?: string) {
    const variationIndex = this.variations.findIndex((v) => v._key === variation._key);
    if (variationIndex === -1) {
      return this;
    }

    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('rules', (rules) => {
        const index = rules.findIndex((r) => r._key === rule._key);
        if (index === -1) {
          return rules;
        }
        return rules.update(index, (updatedRule) =>
          setRolloutForRule(updatedRule, this, variation, weight, contextKind),
        );
      }),
    );
  }

  setRuleExperimentRollout(
    environmentKey: string,
    rule: Rule,
    rolloutData: List<WeightedVariation>,
    experimentData: ExperimentRollout,
  ) {
    const variations = this.variations;
    //check that all variations exist
    let variationExists = true;
    rolloutData.forEach((data) => {
      const variation = variations.get(data.variation);
      if (!variation && variationExists) {
        variationExists = false;
      }
    });

    if (!variationExists) {
      return this;
    }

    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('rules', (rules) => {
        const index = rules.findIndex((r) => r._key === rule._key);
        if (index === -1) {
          return rules;
        }

        return rules.update(index, (updatedRule) =>
          updatedRule
            .delete('variation')
            .update('rollout', (r) => {
              const experimentRolloutInfo = { experimentAllocation: experimentData };
              return r && r.experimentAllocation
                ? r.set('experimentAllocation', experimentData)
                : createRollout(experimentRolloutInfo);
            })
            .update('rollout', (r) => {
              /* eslint-disable @typescript-eslint/no-non-null-assertion */
              // We created the rollout above so we can safely assert it exists here
              let newR = r!; /* eslint-enable @typescript-eslint/no-non-null-assertion */
              rolloutData.forEach((data) => {
                newR = handleRolloutLogic(newR, data.variation, data.weight, variations);
              });
              return newR;
            }),
        );
      }),
    );
  }

  editRuleDescription(environmentKey: string, rule: Rule, description: string) {
    return this.updateConfiguration(environmentKey, (cfg) => {
      const index = cfg.rules.findIndex((r) => r._key === rule._key);
      if (index === -1) {
        return cfg;
      }
      return cfg.setIn(['rules', index, 'description'], description);
    });
  }

  editRuleTrackEvents(environmentKey: string, rule: Rule, value: boolean) {
    return this.updateConfiguration(environmentKey, (cfg) => {
      const index = cfg.rules.findIndex((r) => r._key === rule._key);
      if (index === -1) {
        return cfg;
      }
      return cfg.setIn(['rules', index, 'trackEvents'], value);
    });
  }

  disableRule(environmentKey: string, rule: Rule) {
    return this.updateConfiguration(environmentKey, (cfg) => {
      const index = cfg.rules.findIndex((r) => r._key === rule._key);
      if (index === -1) {
        return cfg;
      }
      return cfg.setIn(['rules', index, 'disabled'], true);
    });
  }

  enableRule(environmentKey: string, rule: Rule) {
    return this.updateConfiguration(environmentKey, (cfg) => {
      const index = cfg.rules.findIndex((r) => r._key === rule._key);
      if (index === -1) {
        return cfg;
      }
      return cfg.setIn(['rules', index, 'disabled'], false);
    });
  }

  setRuleBucket(environmentKey: string, rule: Rule, bucketBy?: string, contextKind?: string) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('rules', (rules) => {
        const index = rules.findIndex((r) => r._key === rule._key);
        if (index === -1) {
          return rules;
        }

        return rules.update(index, (r) =>
          r
            .delete('variation')
            .update('rollout', (ro) =>
              ro
                ? ro.set('bucketBy', bucketBy).set('contextKind', contextKind || '')
                : createRollout({ bucketBy, contextKind }),
            ),
        );
      }),
    );
  }

  addRuleClause(environmentKey: string, rule: Rule, clause?: Clause) {
    return this.updateConfiguration(environmentKey, (cfg) => {
      const index = cfg.rules.findIndex((r) => r._key === rule._key);
      if (index === -1) {
        return cfg;
      }

      const ruleClauses = cfg.rules.get(index)?.clauses;

      let contextKind: string = ContextKindKey.USER;

      if (!!ruleClauses && !ruleClauses?.isEmpty()) {
        contextKind = ruleClauses.last<Clause>().contextKind;
      }

      return cfg.updateIn(['rules', index], (r: Rule) =>
        r.update('clauses', (cs) => cs.push(clause || createClause({ contextKind }))),
      );
    });
  }

  deleteRuleClause(environmentKey: string, rule: Rule, clause: Clause) {
    return this.updateConfiguration(environmentKey, (cfg) => {
      const index = cfg.rules.findIndex((r) => r._key === rule._key);
      if (index === -1) {
        return cfg;
      }

      return cfg.updateIn(['rules', index], (r: Rule) => {
        const clauseIndex = r.clauses.findIndex((c) => c._key === clause._key);
        if (clauseIndex === -1) {
          return r;
        }

        return r.update('clauses', (cs) => cs.delete(clauseIndex));
      });
    });
  }

  editRuleClause(environmentKey: string, rule: Rule, clause: Clause) {
    return this.updateConfiguration(environmentKey, (cfg) => {
      const index = cfg.rules.findIndex((r) => r._key === rule._key);
      if (index === -1) {
        return cfg;
      }

      const existingRule = cfg.rules.get(index);

      if (!existingRule) {
        return cfg;
      }

      const clauseIndex = existingRule.clauses.findIndex((c) => c._key === clause._key);
      if (clauseIndex === -1) {
        return cfg;
      }

      return cfg.setIn(['rules', index, 'clauses', clauseIndex], clause);
    });
  }

  findVariationByValue(value: VariationValue) {
    if (isJSONValue(value)) {
      // Handling JSON variation
      return this.variations.find((v) => isEqual(v.value.toJS(), toJS(value)));
    } else {
      // Handling Boolean, String, and Number variations
      const asString = stringifyValue(value);
      return this.variations.find((v) => v.getValueString() === asString);
    }
  }

  toGlobalRep() {
    return fromJS(this.toJSON()).withMutations(
      (
        map: ImmutableMap<FlagType>,
        // map: ImmutableMap<
        //   Omit<FlagType, 'customProperties'> & { customProperties: OrderedMap<string, CustomProperty> }
        // >,
      ) => {
        if (map.get('maintainerId') === null) {
          map.delete('maintainerId');
        }
        if (map.get('maintainerTeamKey') === null) {
          map.delete('maintainerTeamKey');
        }
        map.update('environments', (es: Map<string, FlagConfiguration>) => es.map((e) => e.toRep()));
        map.update('variations', (vs: List<Variation>) => vs.map((v) => v.toRep()));
        // CHEAT: The UI uses a List<CustomPropertyForm> but the API expects a map[string]CustomProperty.
        //        We use a cast here because we assume toGlobalRep is only called before sending data to the API.
        map.update(
          'customProperties',
          (m) => customPropertiesFormToRep(List(m)) as unknown as List<CustomPropertyForm>,
        );
        map.update('experiments', (experimentInfo) => experimentInfo.toRep());
      },
    );
  }

  getVariationIndexFromId(variationId: string) {
    return getFlagVariationIndexFromId(this.variations.toArray(), variationId);
  }

  getVariationIndexFromValue(variationValue: VariationValue) {
    return this.variations.findIndex((v) => v.value === variationValue);
  }

  canBypassRequiredApprovals(envKey?: string) {
    if (envKey) {
      const flagConfig = this.environments.get(envKey);
      for (const access of flagConfig?._access?.get('allowed') ?? []) {
        if (access.get('action') === 'bypassRequiredApproval') {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Determines whether a flag will require approval for changes
   * based off of a given environment's settings
   */
  isApprovalRequired(approvalSettings?: ApprovalSettings): boolean {
    if (!approvalSettings) {
      return false;
    }
    if (approvalSettings.required) {
      return true;
    }
    // This mirrors the AreApprovalsRequiredByTag method on the backend
    // https://github.com/launchdarkly/gonfalon/blob/master/internal/shared/approvals/check_tags.go#L3
    if (approvalSettings.requiredApprovalTags.size > 0) {
      const requiredApprovalTagsMap = approvalSettings.requiredApprovalTags.reduce(
        (accum, tag) => ({ ...accum, [tag]: true }),
        {} as { [key: string]: boolean },
      );
      for (const tag of this.tags) {
        if (requiredApprovalTagsMap.hasOwnProperty(tag)) {
          return true;
        }
      }
    }
    return false;
  }

  hasProjectDefaults() {
    return (
      this.getIn(['defaults', 'onVariation']) === 1 ||
      this.getIn(['defaults', 'offVariation']) === 0 ||
      this.getIn(['clientSideAvailability', 'usingEnvironmentId']) ||
      this.getIn(['clientSideAvailability', 'usingMobileKey']) ||
      this.variations.get(0)?.name !== '' ||
      this.variations.get(0)?.description !== '' ||
      this.variations.get(1)?.name !== '' ||
      this.variations.get(1)?.description !== '' ||
      !this.temporary ||
      !this.tags.isEmpty()
    );
  }

  /**
   * Returns the flag updated to match the configuration settings
   * of the provided original flag
   */
  resetConfigurationSetting(originalFlag: Flag, setting: FlagConfigSetting, environmentKey: string) {
    const originalConfiguration = originalFlag.getConfiguration(environmentKey);
    if (!originalConfiguration) {
      return this;
    }
    const originalSettingValue = originalConfiguration.get(setting);
    return this.updateConfiguration(environmentKey, (cfg) => cfg.set(setting, originalSettingValue));
  }

  preserveFlagContextsEvaluation(flagWithEvaluationInfo: Flag) {
    let updatedFlag: Flag | undefined;
    flagWithEvaluationInfo.environments.forEach((config, environmentKey) => {
      if (this.environments.has(environmentKey)) {
        updatedFlag = this.updateConfiguration(environmentKey, (cfg) => cfg.set('evaluation', config.evaluation));
      }
    });
    return updatedFlag || this;
  }

  countIndividualTargetsForAllVariations(envKey: string) {
    const config = this.getConfiguration(envKey);
    return this.variations
      .map((__, idx) => (config ? config.getCountOfTargetsForVariation(idx) : 0))
      .reduce((a, b) => a + b, 0);
  }

  canDeprecateFlag() {
    return canDeprecateFlag(this.toJS());
  }

  isFlagDeprecated() {
    return isFlagDeprecated(this.toJS());
  }

  isAiConfigFlag() {
    return isAiConfigFlag(this._purpose);
  }

  getConfigValidation(envKey: string) {
    const config = this.getConfiguration(envKey);
    return validateFlagConfiguration(config, this.variations, {
      userTargetAlertThreshold: userTargetAlertThreshold(),
      userTargetWarningThreshold: userTargetWarningThreshold(),
    });
  }

  setRuleMeasuredRolloutConfig(environmentKey: string, rule: Rule, config?: MeasuredRolloutConfig) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('rules', (rules) => {
        const index = rules.findIndex((r) => r._key === rule._key);
        if (index === -1) {
          return rules;
        }

        return rules.update(index, (r) => r.set('measuredRolloutConfig', config));
      }),
    );
  }

  setFallthroughMeasuredRolloutConfig(environmentKey: string, config?: MeasuredRolloutConfig) {
    return this.updateConfiguration(environmentKey, (cfg) =>
      cfg.update('fallthrough', (fallthrough) => fallthrough.set('measuredRolloutConfig', config)),
    );
  }

  isMeasuredRollout(environmentKey: string) {
    return (
      this.getRules(environmentKey)?.some((rule) => Boolean(rule.measuredRolloutConfig)) ||
      !!this.getFallthrough(environmentKey)?.measuredRolloutConfig
    );
  }

  getGuardedReleaseConfig(environmentKey: string) {
    return (
      this.getRules(environmentKey)?.find((rule) => Boolean(rule.measuredRolloutConfig))?.measuredRolloutConfig ||
      this.getFallthrough(environmentKey)?.measuredRolloutConfig
    );
  }

  updateTargeting(environmentKey: string, variation: Variation, keys: List<string>, contextKind: string) {
    return this.updateConfiguration(environmentKey, (cfg) => {
      const variationIndex = this.variations.findIndex((v) => v._key === variation._key);
      const targetType = contextKind === 'user' ? 'targets' : 'contextTargets';
      const index = cfg[targetType].findIndex((t) => t.variation === variationIndex && t.contextKind === contextKind);

      let updatedConfig;
      if (keys.isEmpty()) {
        if (index >= 0) {
          updatedConfig = cfg.deleteIn([targetType, index]);
        } else {
          updatedConfig = cfg;
        }
      } else if (index >= 0) {
        updatedConfig = cfg.updateIn([targetType, index], (t) => t.set('values', keys));
      } else {
        const target = createTarget({
          variation: variationIndex,
          values: keys,
          contextKind,
        });
        updatedConfig = cfg.update(targetType, (ts) => ts.push(target));
      }

      // Remove the keys from any other variation's targets
      updatedConfig = updatedConfig.update(targetType, (targets) =>
        targets
          .map((target) => {
            if (target.variation !== variationIndex && target.contextKind === contextKind) {
              // Remove the keys from other variations with the same context kind
              return target.update('values', (values) => values.filter((value) => !keys.includes(value)));
            }
            return target;
          })
          // Remove any targets that end up with empty values
          .filter((target) => !target.values.isEmpty()),
      );

      return updatedConfig;
    });
  }
}

export function createFlag(props: CreateFunctionInput<Flag> = {}): Flag {
  let flag = props instanceof Flag ? props : new Flag(fromJS(props));

  flag = flag.withMutations((f) => {
    f.update('variations', (vs) => vs.map(createVariation));
    f.update('defaults', (ds) => (ds ? new Defaults(ds) : undefined));
    f.update('environments', (es) => es.map(createFlagConfiguration));
    f.update('tags', (tags) => tags.toSet());
    f.update('goalIds', (goalIds) => goalIds.toSet());
    f.update('_maintainer', (m) => (m ? createMemberSummary(m) : undefined));
    f.update('_maintainerTeam', (m) => (m ? createTeam(m) : undefined));
    // CHEAT: The API returns a map[string]CustomProperty, but we deal with a List<CustomPropertyForm> in the UI
    //        We use a cast here because we assume createFlag is only called with data from the API.
    f.update('customProperties', (customProperties) =>
      customPropertiesToForm(customProperties as unknown as Map<string, CustomProperty>),
    );
    f.update('experiments', (e) => createExperimentInfo(e.toJS()));
    f.update('codeReferences', (c) => (c ? createAllCodeRefStatsRecord(c) : undefined));
  });

  return flag;
}
